<template>
  <Transition name="reconnecting-banner">
    <div
      v-if="visible"
      class="reconnecting-banner"
      role="status"
      aria-live="polite"
      data-testid="reconnecting-banner"
    >
      <span class="reconnecting-banner__spinner" aria-hidden="true" />
      <span class="reconnecting-banner__text">{{ t('system.reconnecting') }}</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConnectionStore } from '@/app/stores/connection';

const { t } = useI18n();
const connection = useConnectionStore();
const { state } = storeToRefs(connection);
const visible = computed(() => state.value === 'reconnecting');
</script>

<style scoped>
.reconnecting-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  gap: 0.75em;
  padding: max(10px, env(safe-area-inset-top)) clamp(14px, 2vw, 28px) 10px;
  background:
    linear-gradient(
      180deg,
      rgba(var(--Kiosk--tint-inverse-color-rgb, 255, 255, 255), 0.08),
      transparent
    ),
    color-mix(
      in srgb,
      var(--Kiosk--button_background_color, var(--Kiosk--font_color, #ffffff)) 92%,
      transparent
    );
  color: var(
    --Kiosk--button_font_color,
    var(--Kiosk--background_color, var(--Kiosk--tint-inverse-color, #000000))
  );
  font-family: var(--Kiosk--font_regular_url, var(--Global--font));
  font-size: clamp(14px, var(--Kiosk--font_size, 100%), 22px);
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  text-transform: var(--Kiosk--font_capitalize, none);
  pointer-events: none;
  border-bottom: 1px solid color-mix(in srgb, currentColor 28%, transparent);
  box-shadow: 0 0.25rem 1.1rem rgba(var(--Kiosk--background_color-rgb, 0, 0, 0), 0.24);
  backdrop-filter: blur(6px);
}

.reconnecting-banner__text {
  overflow-wrap: anywhere;
  letter-spacing: 0.02rem;
}

.reconnecting-banner__spinner {
  flex: 0 0 auto;
  width: 1.125em;
  height: 1.125em;
  border: 0.14em solid color-mix(in srgb, currentColor 30%, transparent);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: reconnecting-banner-spin 0.9s linear infinite;
}

@keyframes reconnecting-banner-spin {
  to {
    transform: rotate(360deg);
  }
}

.reconnecting-banner-enter-active,
.reconnecting-banner-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.reconnecting-banner-enter-from,
.reconnecting-banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
